// R11P GEOLOGICAL P0.5-P9E — Investor GIS entitlement & projection core.
//
// Pure and dependency-free (no React, no network, no `@/` imports) so its
// rules run behaviourally under `node --test`.
//
// The ONLY spatial authority is the server entitlement contract
//   GET /api/gis/investor/me/layers  ->  { layers, count }
// (geo-api P9E-B1). Nothing here derives access from role, investor status,
// profile, application, agreement, document grant, classification, URL or
// client state. Each operation flag is used exactly as returned: view never
// implies download or export, and none of them is computed locally.
//
// State invariants:
//   • UNAVAILABLE ≠ NO ACCESS — a failed read never becomes an empty catalogue;
//   • NO ACCESS is a successful response with no investor-authorized viewable
//     layer. It names no reason: the contract omits expired/revoked grants, so
//     absence can never be presented as expiry, revocation or denial;
//   • a response is applied only to the identity that requested it, so one
//     investor's catalogue can never surface after an account change.

export type InvestorGisAccess = 'investor' | 'public';

export interface InvestorGisOperations {
  view: boolean;
  download: boolean;
  export: boolean;
}

/** The investor-safe layer contract. Unknown server fields never get here. */
export interface InvestorGisLayer {
  id: string;
  slug: string;
  name: string;
  description: string;
  kind: string | null;
  geometryType: string | null;
  classification: string | null;
  access: InvestorGisAccess;
  operations: InvestorGisOperations;
  expiresAt: string | null;
}

export type EntitlementState =
  | 'loading'
  | 'available' // ≥1 layer with access 'investor' AND operations.view
  | 'no_access' // successful response, no such layer — NOT an error
  | 'profile_required' // 403 INVESTOR_PROFILE_REQUIRED
  | 'auth_required' // 401 / session lost
  | 'unavailable'; // transport, 5xx, unexpected status or malformed body

export interface EntitlementSnapshot {
  /** The identity this snapshot belongs to (null = signed out). */
  userId: string | null;
  state: EntitlementState;
  /** Server-returned layers (public + investor). Empty unless a 200 parsed. */
  layers: InvestorGisLayer[];
}

/** The minimum a transport result must carry. */
export interface ApiLikeResult {
  status: number;
  data: unknown;
  error: string | null;
  code?: string | null;
}

export const PROFILE_REQUIRED_CODE = 'INVESTOR_PROFILE_REQUIRED';

const isObj = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);
const str = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v.trim() : null;

/** WHITELIST projection of one entitlement row; null when it is not well-formed. */
export function projectEntitledLayer(raw: unknown): InvestorGisLayer | null {
  if (!isObj(raw)) return null;
  const id = str(raw.id);
  if (!id) return null;
  if (raw.access !== 'investor' && raw.access !== 'public') return null;
  const ops = isObj(raw.operations) ? raw.operations : null;
  if (!ops) return null;
  return {
    id,
    slug: str(raw.slug) ?? id,
    name: str(raw.name) ?? 'Untitled layer',
    description: str(raw.description) ?? '',
    kind: str(raw.kind),
    geometryType: str(raw.geometryType),
    classification: str(raw.classification),
    access: raw.access,
    // Strict: only a literal `true` grants an operation.
    operations: {
      view: ops.view === true,
      download: ops.download === true,
      export: ops.export === true,
    },
    expiresAt: str(raw.expiresAt),
  };
}

/** Layers the server returned with investor authorization AND view. */
export function investorAuthorizedLayers(layers: InvestorGisLayer[]): InvestorGisLayer[] {
  return layers.filter((l) => l.access === 'investor' && l.operations.view === true);
}

/** Every server-returned layer the caller may view (investor or public). */
export function viewableLayers(layers: InvestorGisLayer[]): InvestorGisLayer[] {
  return layers.filter((l) => l.operations.view === true);
}

/** Map one entitlement response to its state and (only on success) its layers. */
export function classifyEntitlement(result: ApiLikeResult): Omit<EntitlementSnapshot, 'userId'> {
  const failed = !!result.error || result.status === 0 || result.status >= 300;
  if (failed) {
    if (result.status === 401) return { state: 'auth_required', layers: [] };
    if (result.status === 403 && result.code === PROFILE_REQUIRED_CODE) {
      return { state: 'profile_required', layers: [] };
    }
    // Any other failure — including a 403 without that code — establishes
    // nothing about entitlement, so it is unavailable, never "no access".
    return { state: 'unavailable', layers: [] };
  }
  const body = isObj(result.data) ? result.data : null;
  if (!body || !Array.isArray(body.layers)) {
    // A 200 whose shape is not the contract is not a measured zero.
    return { state: 'unavailable', layers: [] };
  }
  const layers = body.layers
    .map(projectEntitledLayer)
    .filter((l): l is InvestorGisLayer => l !== null);
  return {
    state: investorAuthorizedLayers(layers).length > 0 ? 'available' : 'no_access',
    layers,
  };
}

export function initialSnapshot(userId: string | null): EntitlementSnapshot {
  return { userId, state: userId ? 'loading' : 'auth_required', layers: [] };
}

export type EntitlementAction =
  | { type: 'identity'; userId: string | null }
  | { type: 'response'; userId: string | null; result: ApiLikeResult }
  | { type: 'auth_lost' };

/**
 * Session-scoped entitlement state.
 *  - an identity change (logout, account switch) discards every layer at once;
 *  - a response is applied ONLY if it was requested by the current identity,
 *    so Investor A's late response can never populate Investor B's view;
 *  - losing authentication discards every layer.
 */
export function reduceEntitlement(
  snap: EntitlementSnapshot,
  action: EntitlementAction,
): EntitlementSnapshot {
  switch (action.type) {
    case 'identity':
      return action.userId === snap.userId ? snap : initialSnapshot(action.userId);
    case 'response':
      if (action.userId !== snap.userId || !snap.userId) return snap;
      return { userId: snap.userId, ...classifyEntitlement(action.result) };
    case 'auth_lost':
      return { userId: snap.userId, state: 'auth_required', layers: [] };
    default:
      return snap;
  }
}

/**
 * Export is offered only when the server says operations.export === true.
 * View and download never imply it. (The geo-api has no separate GIS download
 * route, so `download` is reported by the contract but offers no Portal action.)
 */
export function canExport(layer: InvestorGisLayer): boolean {
  return layer.operations.export === true;
}

/** The Investor GIS entry/CTA rule: server entitlement only. */
export function hasInvestorGis(snap: EntitlementSnapshot): boolean {
  return snap.state === 'available' && investorAuthorizedLayers(snap.layers).length > 0;
}

/* ───────────── features ───────────── */

/**
 * The investor-safe ATTRIBUTE whitelist. These are the properties the governed
 * geological publication path writes into a GIS feature (geo-api
 * geologyPublication.service). Internal identifiers it also writes
 * (geological_observation_id, project_id) and every other property — staff,
 * audit, device, notes, grant or arbitrary import attributes — are dropped.
 */
export const SAFE_FEATURE_FIELDS: ReadonlyArray<readonly [string, string]> = [
  ['observation_code', 'Observation code'],
  ['revision', 'Revision'],
  ['classification', 'Classification'],
  ['approved_at', 'Approved'],
];

const MAX_VALUE_LENGTH = 120;

export interface SafeAttribute {
  label: string;
  value: string;
}

export interface InvestorFeature {
  geometry: { type: string; coordinates: unknown };
  attributes: SafeAttribute[];
}

function safeValue(key: string, v: unknown): string | null {
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  if (typeof v !== 'string' || !v.trim()) return null;
  if (key === 'approved_at') {
    const t = Date.parse(v);
    return Number.isNaN(t) ? null : new Date(t).toISOString().slice(0, 10);
  }
  const s = v.trim();
  return s.length > MAX_VALUE_LENGTH ? `${s.slice(0, MAX_VALUE_LENGTH)}…` : s;
}

/** Project one GeoJSON feature: geometry + whitelisted, primitive attributes only. */
export function projectInvestorFeature(raw: unknown): InvestorFeature | null {
  if (!isObj(raw) || !isObj(raw.geometry)) return null;
  const type = str(raw.geometry.type);
  if (!type) return null;
  const props = isObj(raw.properties) ? raw.properties : {};
  const attributes: SafeAttribute[] = [];
  for (const [key, label] of SAFE_FEATURE_FIELDS) {
    const value = safeValue(key, props[key]);
    if (value !== null) attributes.push({ label, value });
  }
  return { geometry: { type, coordinates: raw.geometry.coordinates }, attributes };
}

export type FeatureState =
  | { kind: 'loading' }
  | { kind: 'ready'; features: InvestorFeature[] }
  | { kind: 'empty' }
  | { kind: 'denied' } // 403 — clear the layer; never shown as empty
  | { kind: 'auth_required' }
  | { kind: 'unavailable' };

/** Map one feature read to its state. A failure is never zero features. */
export function classifyFeatures(result: ApiLikeResult): FeatureState {
  const failed = !!result.error || result.status === 0 || result.status >= 300;
  if (failed) {
    if (result.status === 401) return { kind: 'auth_required' };
    if (result.status === 403) return { kind: 'denied' };
    return { kind: 'unavailable' };
  }
  const body = isObj(result.data) ? result.data : null;
  if (!body || !Array.isArray(body.features)) return { kind: 'unavailable' };
  const features = body.features
    .map(projectInvestorFeature)
    .filter((f): f is InvestorFeature => f !== null);
  return features.length ? { kind: 'ready', features } : { kind: 'empty' };
}

/**
 * Whether a feature request may be issued at all. A layer id from a route,
 * query string or anywhere else is an identifier, not authority: only a layer
 * the server returned with operations.view === true is ever requested.
 */
export function mayRequestFeatures(layers: InvestorGisLayer[], layerId: string): boolean {
  return viewableLayers(layers).some((l) => l.id === layerId);
}
